import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Seo from "../components/Seo";
import { Button } from "../components/ui/button";
import { useApp } from "../context/AppContext";
import { useReveal } from "../hooks/useReveal";
import { getProduct } from "../services/api";
import { useToast } from "../hooks/use-toast";
import { ArrowLeft, Plus } from "lucide-react";

const ProductDetail = () => {
  const { id } = useParams();
  const { t, lang, addToCart } = useApp();
  const { toast } = useToast();
  const [p, setP] = useState(null);
  useReveal([p, lang]);

  useEffect(() => { getProduct(id).then(setP).catch(() => setP(false)); }, [id]);

  if (p === null) return <div className="pt-40 text-center text-ink/50">…</div>;
  if (p === false) return <div className="pt-40 text-center text-ink/50">404 · <Link to="/store" className="text-wine underline">{t("store.title")}</Link></div>;

  const name = lang === "de" && p.name_de ? p.name_de : p.name;
  const descHtml = p.description ? (p.description[lang] || p.description.en) : "";
  const base = "https://myowncuvee.com";
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name,
    image: p.img ? (p.img.startsWith("http") ? p.img : base + p.img) : base + "/og-image.jpg",
    description: (p.desc && (p.desc[lang] || p.desc.en)) || name,
    sku: p.id,
    brand: { "@type": "Brand", name: "My Own Cuvée" },
    offers: {
      "@type": "Offer",
      url: `${base}/product/${p.id}`,
      priceCurrency: "CHF",
      price: Number(p.price).toFixed(2),
      availability: "https://schema.org/InStock",
      itemCondition: "https://schema.org/NewCondition",
    },
  };

  const handleAdd = () => {
    addToCart({ id: p.id, name, price: p.price, img: p.img, category: p.category });
    toast({ title: t("common.added"), description: name });
  };

  return (
    <div className="pt-20 bg-cream min-h-screen">
      <Seo title={name} description={(p.desc && (p.desc[lang] || p.desc.en)) || name} image={p.img} path={`/product/${p.id}`} type="product" lang={lang} jsonLd={jsonLd} />
      <div className="max-w-6xl mx-auto px-6 py-14">
        <Link to="/store" className="inline-flex items-center gap-1.5 text-wine hover:text-wine-dark text-sm font-medium mb-8"><ArrowLeft className="w-4 h-4" /> {t("store.title")}</Link>
        <div className="grid md:grid-cols-2 gap-12 items-start">
          <div className="reveal rounded-3xl overflow-hidden bg-white border border-border shadow-md">
            <img src={p.img || "/logo_moc.png"} alt={name} className="w-full h-[520px] object-cover" />
          </div>
          <div className="reveal">
            <span className="inline-block bg-wine/10 text-wine text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full">
              {p.type === "red" ? t("store.red") : t("store.white")} · {p.category === "starter" ? t("store.starter") : t("store.single")}
            </span>
            <h1 className="font-serif text-4xl text-ink mt-4">{name}</h1>
            <p className="font-serif text-3xl text-wine mt-4">{t("common.currency")} {Number(p.price).toFixed(2)}</p>
            <Button onClick={handleAdd} className="mt-6 h-12 px-8 bg-wine hover:bg-wine-dark text-white rounded-full gap-2"><Plus className="w-5 h-5" /> {t("common.addToCart")}</Button>
            {descHtml && (
              <div className="blog-content mt-10 text-ink/80 leading-relaxed" dangerouslySetInnerHTML={{ __html: descHtml }} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
